<template>
    <GoogleMap 
        ref="map" 
        :api-key="effectiveGoogleApi.mapKey" 
        mapId="78754c129d1b9610c870d567"
        :center="center" 
        :zoom="zoom" 
        class="map" 
        @click="expandMap" 
        :zoomControl="true"
        :mapTypeControl="false"
        :scaleControl="false"
        :streetViewControl="false"
        :rotateControl="false"
        :fullscreenControl="true"
        :disableDefaultUI="false"
        :mapTypeId="'roadmap'"
        :libraries="['places', 'marker']">
        <AdvancedMarker 
            v-for="marker in mapMarkers" 
            :key="marker.id" 
            :options="{
                position: marker.position,
                title: marker.title
            }"
            @click="onMapMarkerClick(marker.id)">
            <template #content>
                <img 
                    :src="marker.icon.url" 
                    :style="{
                        width: marker.icon.scaledSize?.width + 'px' || '30px',
                        height: marker.icon.scaledSize?.height + 'px' || '30px',
                        display: marker.visible ? 'block' : 'none'
                    }"
                    :alt="marker.title"
                />
            </template>
        </AdvancedMarker>
    </GoogleMap>
</template>

<script>
import { mapActions } from "vuex";
import { inject } from "vue";
import EventBus from "../functions/eventBus";
import { googleApi, defaultOpt } from "../../../config/map";


// Map Styles
const BRANCH_MARKER = "/assets/images/branch.svg";
const ATM_MARKER = "/assets/images/atm.svg";

export default {
    setup() {
        // Inject Google Maps configuration if available (fallback to imported config)
        const injectedApiKey = inject('googleMapsApiKey', null);
        const injectedConfig = inject('googleMapsConfig', null);
        
        return {
            injectedApiKey,
            injectedConfig
        };
    },
    created() {
        EventBus.on("mapBound", () => {
            this.newSearch = true;
        });
    },
    data() {
        return {
            center: defaultOpt.pos,
            zoom: defaultOpt.zoom,
            mapMarkers: null,
            mapMarkerIconSize: null,
            newSearch: false,
            showSearch: false,
            googleApi: googleApi
        }
    },
    mounted() {
        // Wait for Google Maps API to be loaded
        this.$nextTick(() => {
            this.initializeMapIcons();
        });
    },
    computed: {
        effectiveGoogleApi() {
            return {
                mapKey: this.injectedApiKey || googleApi.mapKey
            };
        },
        filteredLocations: {
            get() {
                return this.$store.getters.filteredLocations;
            }
        },
        isSpecificLocation: {
            get() {
                return this.$store.getters.isSpecificLocation;
            }
        },
        selectedLocation: {
            get() {
                return this.$store.getters.selectedLocation;
            },
            set(value) {
                this.updateSelectedLocation(value);
            }
        },
        userLocation: {
            get() {
                return this.$store.getters.userLocation;
            }
        },
        userSearch: {
            get() {
                return this.$store.getters.userSearch;
            }
        }
    },
    methods: {
        ...mapActions(["updateSelectedLocation", "updateLocations"]),

        initializeMapIcons() {
            // Set default icon sizes for AdvancedMarker
            this.mapMarkerIconSize = { width: 30, height: 30 };
            this.mapMarkerSelectedSize = { width: 50, height: 50 };
        },

        addMarkers(locations) {
            let markers = {};
            for (let i = 0; i < locations.length; i++) {
                let location = locations[i];
                if (location === null) {
                    console.log('location was null');
                    continue;
                }

                const marker = {};
                marker.id = location.id;
                marker.index = i + 1;
                marker.title = location.name;
                marker.visible = true;

                marker.position = {
                    lat: location.geocode.latitude,
                    lng: location.geocode.longitude
                };

                marker.icon = {
                    url: this.getMapIcon(location),
                    scaledSize: this.mapMarkerIconSize
                };

                markers[location.id] = marker;
            }
            this.mapMarkers = markers;

            //Only bound map on new search location
            if (this.newSearch) {
                setTimeout(() => {
                    this.boundMarkers();
                }, 0);
            }
        },
        boundMarkers() {
            if (this.$refs.map && this.$refs.map.map && typeof google !== 'undefined' && google.maps) {
                const propOwn = Object.getOwnPropertyNames(this.mapMarkers);
                if (propOwn.length >= 2) {
                    const bounds = new window.google.maps.LatLngBounds();
                    for (var m in this.mapMarkers) {
                        if (this.mapMarkers.hasOwnProperty(m)) {
                            let lat = parseFloat(this.mapMarkers[m].position.lat);
                            let lng = parseFloat(this.mapMarkers[m].position.lng);
                            let myLatlng = new window.google.maps.LatLng(lat, lng);
                            bounds.extend(myLatlng);
                        }
                    }

                    // Don't zoom in too far on only one marker
                    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
                        var extendPoint1 = new google.maps.LatLng(bounds.getNorthEast().lat() + 0.001, bounds.getNorthEast().lng() + 0.001);
                        var extendPoint2 = new google.maps.LatLng(bounds.getNorthEast().lat() - 0.001, bounds.getNorthEast().lng() - 0.001);
                        bounds.extend(extendPoint1);
                        bounds.extend(extendPoint2);
                    }

                    // Access the map instance through the component ref
                    this.$refs.map.map.fitBounds(bounds);
                    // Prevent bounding on filtering
                    this.newSearch = false;
                } else if (propOwn.length === 1) {
                    // For single marker, just center and zoom to it
                    const markerId = propOwn[0];
                    const marker = this.mapMarkers[markerId];
                    if (marker && marker.position) {
                        const markerPosition = {
                            lat: marker.position.lat,
                            lng: marker.position.lng
                        };
                        this.zoom = 18;
                        this.$refs.map.map.setZoom(18);
                        this.$refs.map.map.setCenter(markerPosition);
                    }
                    this.newSearch = false;
                } else {
                    // No results no map bound, center on requested search
                    if (this.userSearch && this.userSearch.lat && this.userSearch.lng) {
                        this.panTo(this.userSearch);
                    } else if (this.userLocation && this.userLocation.lat && this.userLocation.lng) {
                        this.panTo(this.userLocation);
                    }
                }
            } else {
                // Retry if map isn't ready yet
                setTimeout(() => {
                    this.boundMarkers();
                }, 100);
            }
        },
        contractMap() {
            if (this.$mq !== 'desktop') {
                this.mobileExpandMap = false;
                EventBus.expandMap(false);
                setTimeout(() => {
                    EventBus.scrollToTopMobile();
                }, 600);
            }
        },
        expandMap() {
            if (this.selectedLocation) {
                EventBus.scrollToTopMobile(0);
            }
            this.mobileExpandMap = true;
            EventBus.expandMap(true);
            EventBus.scrollToTopMobile(0);
        },
        getMapIcon(location) {
            if (location.isAtm) {
                return ATM_MARKER;
            }
            return BRANCH_MARKER;
        },
        setMapMarkerVisibility(id, show = false) {
            for (let m in this.mapMarkers) {
                let marker = this.mapMarkers[m];

                if (marker.id != id) {
                    marker["visible"] = show;
                }
            }
        },
        panBack(location) {
            this.$refs.map.panTo(this.center);
        },
        panTo(latlng, zoom = defaultOpt.zoom) {
            if (latlng && this.$refs.map && this.$refs.map.map) {
                this.$refs.map.map.setZoom(zoom);
                this.$refs.map.map.panTo({ lat: latlng.lat, lng: latlng.lng });
            }
        },
        onMapMarkerClick(id) {
            if (!this.isSpecificLocation) {
                let selectedLoc = this.filteredLocations.find(item => item.id == id);
                window.location = selectedLoc.locationUrl;
            }
        },
        onMapMarkerHover(id, mouseOut) {
            if (!this.mapMarkers[id] || this.isSpecificLocation) return;

            const icon = {
                url: this.getMapIcon(this.mapMarkers[id]),
                scaledSize: mouseOut ? this.mapMarkerIconSize : this.mapMarkerSelectedSize
            };

            this.mapMarkers[id].icon = icon;
        },
        selectMapMarker(id, active) {
            if (this.mapMarkers && this.mapMarkers[id]) {
                if (!this.mapMarkers[id]) return;

                this.setMapMarkerVisibility(id);

                if (active) {
                    this.panTo(this.mapMarkers[id].position, 18);
                }
            }
        }
    },
    watch: {
        filteredLocations(newValue) {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.addMarkers(newValue);
                }, 500);
            });
        },

        mapMarkers(markers) {
            this.boundMarkers();
        },

        selectedLocation(newValue, oldValue) {
            if (newValue != null) {
                this.selectMapMarker(oldValue ? oldValue.id : null, false);
                this.selectMapMarker(newValue.id, true);

                if (this.isSpecificLocation) {
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.addMarkers([newValue]);
                            this.setMapMarkerVisibility(null, true);
                            this.boundMarkers();
                            if (this.$refs.map && this.$refs.map.map && newValue.geocode) {
                                this.$refs.map.map.panTo({
                                    lat: newValue.geocode.latitude,
                                    lng: newValue.geocode.longitude
                                });
                            }
                        }, 500);
                    });
                }
            }

        },
        userSearch(newValue) {
            if (this.filteredLocations.length == 0) {
                if (newValue.lat && newValue.lng) {
                    this.center = newValue;
                }
            }
            // Flag for new search
            this.newSearch = true;
        }
    }
}
</script>
